import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  MdLocalHospital, MdAddBusiness, MdAnalytics, MdHistory,
  MdAdminPanelSettings, MdLogin, MdLogout, MdMenu, MdClose,
  MdDashboard, MdLink, MdBlock, MdArchive, MdRefresh, MdPrivacyTip,
  MdSupportAgent, MdMonitorHeart,
} from 'react-icons/md';
import { usePlatformAuth } from '../auth/PlatformAuthContext';

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { to: '/platform/dashboard', label: 'Hospitals', icon: MdDashboard, end: true },
      { to: '/platform/dashboard?status=all', label: 'All hospitals', icon: MdLocalHospital },
      { to: '/platform/dashboard?status=suspended', label: 'Suspended', icon: MdBlock },
      { to: '/platform/dashboard?status=archived', label: 'Archived', icon: MdArchive },
      { to: '/platform/onboard', label: 'Onboard hospital', icon: MdAddBusiness },
    ],
  },
  {
    label: 'Monitoring',
    items: [
      { to: '/platform/analytics', label: 'Analytics & revenue', icon: MdAnalytics },
      { to: '/platform/audit', label: 'Fleet audit log', icon: MdHistory },
      { to: '/platform/dashboard', label: 'OP link status', icon: MdLink, hint: 'See OP LINK column on hospitals' },
    ],
  },
  {
    label: 'HCEN company',
    items: [
      { to: '/platform/admins', label: 'HCEN admins', icon: MdAdminPanelSettings },
      { to: '/login', label: 'Staff login (hospital)', icon: MdLogin, external: true },
      { to: '/privacy', label: 'Privacy policy', icon: MdPrivacyTip },
    ],
  },
];

function navActive(pathname, search, item) {
  if (item.external) return false;
  const [path, query] = item.to.split('?');
  if (pathname !== path && !pathname.startsWith(`${path}/`)) return false;
  if (!query) {
    // Exact dashboard home: active only when no status filter (or status=active)
    if (path === '/platform/dashboard' && item.end) {
      const st = new URLSearchParams(search).get('status');
      return !st || st === 'active';
    }
    if (path === '/platform/dashboard' && item.label === 'OP link status') {
      return false; // never steal active from Hospitals
    }
    return pathname === path || pathname.startsWith(`${path}/`);
  }
  const want = new URLSearchParams(query).get('status');
  const have = new URLSearchParams(search).get('status');
  return pathname === path && have === want;
}

export default function PlatformShell({ title, subtitle, children, actions }) {
  const { user, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  const logoSrc = `${process.env.PUBLIC_URL || ''}/hcen_logo.png`;

  return (
    <div className={`platform-shell${mobileOpen ? ' is-mobile-open' : ''}`}>
      <header className="platform-mobile-bar">
        <button type="button" className="platform-mobile-menu" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
          <MdMenu size={22} />
        </button>
        <div className="platform-mobile-brand">
          <img src={logoSrc} alt="" />
          <span>HCEN Platform</span>
        </div>
      </header>

      {mobileOpen && (
        <button type="button" className="platform-nav-backdrop" aria-label="Close menu" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`platform-sidebar${mobileOpen ? ' is-open' : ''}`}>
        <div className="platform-sidebar-brand">
          <span className="platform-sidebar-logo-wrap">
            <img src={logoSrc} alt="HCEN" />
          </span>
          <div>
            <div className="platform-sidebar-title">HCEN Platform</div>
            <div className="platform-sidebar-sub">Networks HMS admin</div>
          </div>
          <button type="button" className="platform-sidebar-close" aria-label="Close" onClick={() => setMobileOpen(false)}>
            <MdClose size={20} />
          </button>
        </div>

        <nav className="platform-sidebar-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label} className="platform-nav-section">
              <div className="platform-nav-section-label">{section.label}</div>
              {section.items.map((item) => {
                const Icon = item.icon || MdMonitorHeart;
                const active = navActive(location.pathname, location.search, item);
                const className = `platform-nav-link${active ? ' active' : ''}`;
                if (item.external || item.to === '/login' || item.to === '/privacy') {
                  return (
                    <a key={item.to + item.label} href={item.to} className={className} title={item.hint || item.label}>
                      <Icon className="platform-nav-ico" />
                      <span>{item.label}</span>
                    </a>
                  );
                }
                return (
                  <Link
                    key={item.to + item.label}
                    to={item.to}
                    className={className}
                    title={item.hint || item.label}
                  >
                    <Icon className="platform-nav-ico" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="platform-sidebar-foot">
          <div className="platform-sidebar-user">
            <span className="platform-sidebar-avatar">{(user?.name || user?.email || 'H').charAt(0).toUpperCase()}</span>
            <div>
              <div className="platform-sidebar-user-name">{user?.name || 'HCEN Owner'}</div>
              <div className="platform-sidebar-user-email">{user?.email || ''}</div>
            </div>
          </div>
          <a href="mailto:support@hcen.in" className="platform-nav-link">
            <MdSupportAgent className="platform-nav-ico" />
            <span>Support</span>
          </a>
          <button
            type="button"
            className="platform-logout"
            onClick={() => { logout(); navigate('/platform/login'); }}
          >
            <MdLogout /> Logout
          </button>
        </div>
      </aside>

      <div className="platform-main">
        <header className="platform-topbar">
          <div>
            <h1 className="topbar-title">{title}</h1>
            <p className="topbar-sub">{subtitle || `${user?.name || user?.email} — HCEN platform owner`}</p>
          </div>
          <div className="topbar-actions">
            {actions}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => window.location.reload()}>
              <MdRefresh /> Refresh
            </button>
          </div>
        </header>
        <div className="page-body platform-page-body">{children}</div>
      </div>
    </div>
  );
}
